// The session journal at run time (SPEC F10 base): the pure rules in src/lib/journal.ts, wired to the real clock,
// an idle timer and the page's visibility. Activity sources call `recordActivity`; nothing else here is visible
// to the user until a check-out is offered (CheckoutHost). A session is stored from its first activity, so a phone
// that kills the app in the background loses nothing: the row already says when the last activity was.
import type { Activity, JournalEvent, JournalState } from '../../lib/journal';
import { emptyJournal, step } from '../../lib/journal';
import { applyJournalWrites } from '../../db/repos/sessions';

const TICK_MS = 60_000;

let state: JournalState = emptyJournal;
let timer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

const newId = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `se${Date.now()}${Math.random().toString(36).slice(2, 8)}`);

function dispatch(event: JournalEvent, now = Date.now()): void {
  const r = step(state, event, now, newId);
  state = r.state;
  if (r.writes.length > 0) void applyJournalWrites(r.writes).catch((err: unknown) => console.warn('Could not save the session', err));
  arm();
}

/** The idle rule needs a clock while a session is open; with none open there is nothing to watch. */
function arm(): void {
  clearTimeout(timer);
  timer = undefined;
  if (!state.open) return;
  timer = setTimeout(() => {
    dispatch({ type: 'tick' });
  }, TICK_MS);
}

/** Something happened in the project: a step checked, a key set, a mark saved to it, a tune applied (SPEC F10). */
export function recordActivity(projectId: string, activity?: Activity): void {
  dispatch({ type: 'activity', projectId, activity });
}

/** The active project changed or was cleared, archived or deleted: that session is over. */
export function leaveProject(): void {
  dispatch({ type: 'leave' });
}

/** Sessions the check-out must not ask about yet: the one you are in, and one in its background grace. */
export function currentSessionIds(): string[] {
  return [state.open?.id, state.hidden?.session.id].filter((id): id is string => Boolean(id));
}

/** Told after the app comes back to the foreground, once the journal has decided whether the session went on. */
export function subscribeForeground(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** The app going to the background ends the session at once (the iOS rule, §10); coming back may continue it (5 min grace). */
export function startJournal(): () => void {
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      dispatch({ type: 'hide' });
      return;
    }
    dispatch({ type: 'show' });
    listeners.forEach((cb) => cb());
  };
  const onHide = () => dispatch({ type: 'hide' });
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onHide);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onHide);
  };
}

/** Tests only. */
export function resetJournalForTests(): void {
  clearTimeout(timer);
  timer = undefined;
  state = emptyJournal;
  listeners.clear();
}
